import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EPropertyTypeId } from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { EAssets } from "@rentiohq/web-shared-next/dist/assets";
import {
  Avatar,
  Box,
  Icon,
  Page,
  Stack,
  Tabs,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import React from "react";
import { Outlet } from "react-router-dom";
import { useLocation, useParams } from "react-router-dom";
import { ProductDetailStyles } from "./PropertyDetails.styles";
import { EPropertyDetailsTabs } from "./propertyDetails.types";
import { getActiveTab } from "./propertyDetails.utils";

const PropertyIcon = {
  [EPropertyTypeId.House]: EAssets.HouseIcon,
  [EPropertyTypeId.Apartment]: EAssets.ApartmentIcon,
  [EPropertyTypeId.StudentResidence]: EAssets.StudentRoomIcon,
  [EPropertyTypeId.Commercial]: EAssets.CommercialPropertyIcon,
  [EPropertyTypeId.Office]: EAssets.OfficeIcon,
  [EPropertyTypeId.ParkingLot]: EAssets.ParkingSpotIcon,
  [EPropertyTypeId.Garage]: EAssets.GarageIcon,
  [EPropertyTypeId.Storage]: EAssets.StorageIcon,
  [EPropertyTypeId.Shed]: EAssets.ShedIcon,
  [EPropertyTypeId.Warehouse]: EAssets.WarehouseIcon,
};

export const PropertyDetails = () => {
  const { pathname } = useLocation();
  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  const sx = ProductDetailStyles();

  const [activeTab, setActiveTab] = React.useState(EPropertyDetailsTabs.Info);

  React.useEffect(() => {
    if (pathname) {
      // @ts-ignore
      setActiveTab(Number(getActiveTab(pathname)));
    }
  }, [pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) =>
    setActiveTab(newValue);

  const { detail: property, isFetching } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  React.useEffect(() => {
    if (property) {
      document.title = `Rentio | ${formatAddress(property, true, true, false)}`;
    }
  }, [property]);

  const tabs = [
    {
      label: getLocalizedText("system.info"),
      tabIndex: EPropertyDetailsTabs.Info,
      link: `/properties/${propertyId}/`,
    },
    {
      label: getLocalizedText("system.contracts"),
      tabIndex: EPropertyDetailsTabs.Contract,
      link: `/properties/${propertyId}/contracts`,
    },
    {
      label: getLocalizedText("system.documents"),
      tabIndex: EPropertyDetailsTabs.Documents,
      link: `/properties/${propertyId}/documents`,
    },
    {
      label: getLocalizedText("system.history"),
      tabIndex: EPropertyDetailsTabs.History,
      link: `/properties/${propertyId}/history`,
    },
  ];

  const renderTitle = () => {
    if (!property) {
      return "";
    }

    return (
      <Stack direction="row" alignItems="center" gap={2.5} my={2.5}>
        {property.images?.length ? (
          <Avatar
            alt="propertyType"
            size="lg"
            sx={sx.content.profileImg}
            src={property.images[0]}
          />
        ) : (
          <Avatar alt="propertyType" size="lg" sx={sx.content.avatar}>
            <Icon
              // @ts-ignore
              path={PropertyIcon[property.typeId]}
              width="48px"
              height="48px"
            />
          </Avatar>
        )}
        <Box>
          <Typography variant="h2">
            {formatAddress(property, true, true, false)}
          </Typography>
          <Typography variant="caption">
            {property?.reference
              ? `${property.reference} | ${property.name}`
              : property.name}
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <Page
      customTitle={renderTitle()}
      isLoading={isFetching}
      breadcrumbLabel="property.detail.breadcrumb"
      breadcrumbLink="/properties"
    >
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
      />
      <Outlet />
    </Page>
  );
};
